import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Divider,
  Button,
  Stack,
  IconButton,
  Box,
  useMediaQuery,
  useTheme,
  TextField,
  Collapse,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { growthProgressQuestions } from '../../constants/checkinQuestions';

const MAX_WORDS_PER_FIELD = 5;

const GrowthProgressDialog = ({ open, onClose, values, onChange, onComplete, onAfterComplete, submitting = false, isCompleted = false }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const questions = growthProgressQuestions;

  const normalizeToMaxWords = (text) => {
    const words = String(text || '').trim().split(/\s+/).filter(Boolean);
    if (words.length <= MAX_WORDS_PER_FIELD) return String(text || '');
    return words.slice(0, MAX_WORDS_PER_FIELD).join(' ');
  };

  const countWords = (text) => {
    return String(text || '').trim().split(/\s+/).filter(Boolean).length;
  };

  const handleFieldChange = (questionId, index, value) => {
    const nextValue = normalizeToMaxWords(value);
    const currentValues = values[questionId] || [''];
    const newValues = [...currentValues];
    newValues[index] = nextValue;
    onChange({
      ...values,
      [questionId]: newValues,
    });
  };

  const handleAddField = (questionId) => {
    const currentValues = values[questionId] || [''];
    if (currentValues.length < 3) {
      onChange({
        ...values,
        [questionId]: [...currentValues, ''],
      });
    }
  };

  const handleRemoveField = (questionId, index) => {
    // Don't allow removing the first (default) field
    if (index === 0) return;
    
    const currentValues = values[questionId] || [''];
    const newValues = currentValues.filter((_, i) => i !== index);
    onChange({
      ...values,
      [questionId]: newValues,
    });
  };

  const handleComplete = async () => {
    try {
      if (onComplete) {
        await onComplete();
      }
      onAfterComplete?.();
      onClose();
    } catch (e) {
      // onComplete should toast / handle errors; keep dialog open
    }
  };

  const handleDialogClose = () => {
    onClose();
  };

  const isComplete = questions.every(q => {
    const vals = values[q.id] || [''];
    return vals.some(v => v && v.trim() !== '');
  });

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      aria-labelledby="growth-progress-title"
      aria-describedby="growth-progress-description"
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          maxHeight: fullScreen ? '100vh' : '90vh',
        },
      }}
    >
      <DialogTitle id="growth-progress-title" sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5" component="span" fontWeight="bold">
            Mid-Day Check-in
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleDialogClose}
            sx={{
              color: 'grey.500',
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} id="growth-progress-description">
            <Box>
              <Typography variant="body1" fontWeight="medium">
                The following three questions will help you to keep track of your growth and progress by 
                capturing concrete highlights from your day.
              </Typography>
            </Box>

            <Divider />

            {questions.map((question, qIndex) => {
              const fieldValues = values[question.id] || [''];
              const canAddMore = fieldValues.length < 3;

              return (
                <Box key={question.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {qIndex + 1}. {question.prompt}
                    </Typography>
                    <IconButton
                      onClick={() => handleAddField(question.id)}
                      disabled={!canAddMore}
                      size="small"
                      color="primary"
                      aria-label="add field"
                      sx={{
                        border: 1,
                        borderColor: canAddMore ? 'primary.main' : 'grey.300',
                        opacity: canAddMore ? 1 : 0.5,
                      }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  
                  <Stack spacing={2}>
                    {fieldValues.map((value, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          placeholder={question.placeholders[index] || `Entry ${index + 1}`}
                          value={value}
                          onChange={(e) => handleFieldChange(question.id, index, e.target.value)}
                          helperText={`Max ${MAX_WORDS_PER_FIELD} words (${countWords(value)}/${MAX_WORDS_PER_FIELD})`}
                          multiline
                          rows={2}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                            },
                          }}
                        />
                        {index > 0 && (
                          <IconButton
                            onClick={() => handleRemoveField(question.id, index)}
                            size="small"
                            color="error"
                            aria-label="remove field"
                            sx={{
                              border: 1,
                              borderColor: 'error.main',
                            }}
                          >
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    ))}
                  </Stack>
                  
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {fieldValues.length}/3 entries
                  </Typography>
                </Box>
              );
            })}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleDialogClose} variant="outlined">
          Close
        </Button>
        <Button 
          onClick={handleComplete} 
          variant="contained" 
          color="primary"
          disabled={!isComplete || submitting}
        >
          {submitting ? 'Saving…' : (isCompleted ? 'Re-submit' : 'Complete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GrowthProgressDialog;
